import React, { useState } from "react";
import clsx from "clsx";
import { Lock, GripVertical, Flag, Pencil, ChevronRight, Repeat } from "lucide-react";
import { fmtDuration, addMinutes, isoNoMs } from "@/lib/format";
import { crewContextFor } from "@/hooks/useGridIndexes";
import StatusPill from "../StatusPill";
import { roomColor } from "../FilterBar";
import TimeEditor from "./cells/TimeEditor";
import RoomDropdown from "./cells/RoomDropdown";
import DurationEditor from "./cells/DurationEditor";
import InlineText from "./cells/InlineText";
import CrewCell from "./CrewCell";
import ContextMenu from "./ContextMenu";
import { ROW_STATE_STYLES, derivedState } from "./rowUtils";
import { makeRoomMoveDragStart, TASK_GROUP_MIME } from "./roomDnd";

/**
 * Full-width schedule block row. Renders the 6 sticky-left cells (start, finish,
 * room, task, status, notes) followed by one CrewCell per visible crew member.
 */
export default function Row({
  block,
  task,
  room,
  ba,
  bb,
  subs,
  isExpanded,
  onToggleExpand,
  crew,
  rowH,
  leftOffsets,
  isSelected,
  isPreviewing,
  startDisplay,
  endDisplay,
  dur,
  driftMin,
  taskStatus,
  onSelect,
  onDragHandle,
  onResize,
  onDragOver,
  onDrop,
  onPatchTask,
  onPatchBlock,
  onAssignCrew,
  onUnassignCrew,
  doubleBookKeys,
  onOpenHistory,
  onSelectTask,
  onEditNotes,
  onDuplicateTask,
  onDeleteTask,
  rooms,
  roomById,
  crewTimelines,
  groupMode = "date",
  selection = [],
  selectionRef,
  onGroupWithTask,
  crewRoles,
  onAddCrewRole,
}) {
  const isLocked = block.is_locked || task?.is_locked;
  const isFloating = task?.is_floating;
  const isDeadline = task?.is_deadline;
  // Drop-zone state for "group with this task" drops.
  const [groupHover, setGroupHover] = useState(false);
  const isRecurringMaster = !!task?.is_recurring && !task?.recurrence_parent_id;
  const isRecurringChild = !!task?.recurrence_parent_id;

  const rowState = derivedState(taskStatus, bb, isLocked);
  const stateStyle = ROW_STATE_STYLES[rowState] || "";

  const [ctxMenu, setCtxMenu] = useState(null);

  const onTitleCommit = (newTitle) => {
    if (newTitle !== task.title) onPatchTask(task.id, { title: newTitle });
  };

  const cellBase = "border-r border-line border-b border-line-subtle flex items-center px-2 text-xs relative";
  const stickyCell = (extra = "") => clsx(cellBase, "bg-bg-base", stateStyle, extra);

  const handleContextMenu = (e) => {
    e.preventDefault();
    setCtxMenu({ x: e.clientX, y: e.clientY });
  };

  const ctxItems = [
    { key: "open", label: "Open details", onClick: () => onSelectTask && onSelectTask() },
    { key: "duplicate", label: "Duplicate task", onClick: () => onDuplicateTask && onDuplicateTask(task), disabled: !task || !!task?.parent_task_id },
    { key: "history", label: "View history", onClick: () => onOpenHistory && onOpenHistory() },
    { divider: true },
    { key: "delete", label: "Delete task", tone: "danger", onClick: () => onDeleteTask && onDeleteTask(task), disabled: !task || isLocked },
  ];

  const blockStartMs = new Date(block.start_at).getTime();
  const blockEndMs = new Date(block.end_at).getTime();

  return (
    <>
      {/* START */}
      <div
        className={stickyCell("drag-handle group")}
        style={{ height: rowH, position: "sticky", left: leftOffsets[0], zIndex: 10 }}
        onClick={onSelect}
        onContextMenu={handleContextMenu}
        onMouseDown={(e) => {
          if (e.button === 0 && e.target?.dataset?.handle === "drag") onDragHandle(e);
        }}
        onDragOver={onDragOver}
        onDrop={onDrop}
        data-testid={`row-${block.id}-start`}
      >
        <div
          className="absolute left-0 top-0 bottom-0 w-3 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
          data-handle="drag"
          title={isLocked ? "Locked — unlock to move" : "Drag to shift in time"}
        >
          <GripVertical className="w-3 h-3 text-ink-mute pointer-events-none" />
        </div>
        {isLocked && <Lock className="w-3 h-3 text-amber mr-1 shrink-0" />}
        <TimeEditor
          value={startDisplay}
          disabled={isLocked}
          onCommit={(newIso) =>
            onPatchBlock(block.id, { start_at: newIso, end_at: isoNoMs(addMinutes(newIso, dur)) })
          }
          dataTestid={`time-start-${block.id}`}
        />
      </div>

      {/* FINISH */}
      <div
        className={stickyCell()}
        style={{ height: rowH, position: "sticky", left: leftOffsets[1], zIndex: 10 }}
        onClick={onSelect}
        onContextMenu={handleContextMenu}
        data-testid={`row-${block.id}-finish`}
      >
        <TimeEditor
          value={endDisplay}
          disabled={isLocked}
          onCommit={(newIso) => {
            if (new Date(newIso) > new Date(block.start_at)) onPatchBlock(block.id, { end_at: newIso });
          }}
          dataTestid={`time-end-${block.id}`}
        />
        {driftMin !== 0 && (
          <span
            className={clsx("drift-chip ml-1", driftMin > 0 ? "text-amber" : "text-cyan")}
            title={`Drift ${driftMin > 0 ? "+" : ""}${driftMin}m`}
          >
            {driftMin > 0 ? "+" : ""}
            {driftMin}m
          </span>
        )}
      </div>

      {/* DUR — typable duration, also adjusts end_at so block grows/shrinks in place */}
      <div
        className={stickyCell()}
        style={{ height: rowH, position: "sticky", left: leftOffsets[2], zIndex: 10 }}
        onClick={onSelect}
        onContextMenu={handleContextMenu}
        data-testid={`row-${block.id}-dur`}
      >
        <DurationEditor
          value={dur}
          disabled={isLocked}
          onCommit={(mins) => {
            const newEnd = isoNoMs(addMinutes(block.start_at, mins));
            // Patch the block (this updates the schedule visually) and the
            // task estimate so reports / future plays of this task carry the
            // new duration.
            onPatchBlock(block.id, { end_at: newEnd });
            if (task?.id) onPatchTask(task.id, { duration_min: mins });
          }}
          dataTestid={`dur-${block.id}`}
        />
      </div>

      {/* ROOM (date view) / DATE+TIME (room view) */}
      <div
        className={stickyCell("pl-0")}
        style={{ height: rowH, position: "sticky", left: leftOffsets[3], zIndex: 10 }}
        onClick={onSelect}
        onContextMenu={handleContextMenu}
        data-testid={`row-${block.id}-room`}
        draggable={groupMode === "room"}
        onDragStart={
          groupMode === "room"
            ? makeRoomMoveDragStart({
                rowKey: block.id,
                selection,
                getSelection: selectionRef ? () => selectionRef.current : undefined,
              })
            : undefined
        }
        title={groupMode === "room" ? "Drag to move to a different room" : undefined}
      >
        <span className="self-stretch w-1 mr-2" style={{ background: roomColor(room?.color_token) }} />
        {groupMode === "room" ? (
          <>
            <GripVertical className="w-3 h-3 text-ink-mute mr-1 shrink-0 opacity-40" />
            <span
              className="mono text-2xs uppercase tracking-widest text-ink tabular-nums truncate"
              title={`${startDisplay}`}
            >
              {fmtDateTimeCompact(startDisplay)}
            </span>
          </>
        ) : (
          <RoomDropdown
            value={block.room_id}
            rooms={rooms}
            disabled={isLocked}
            onChange={(rid) => onPatchBlock(block.id, { room_id: rid })}
          />
        )}
      </div>

      {/* TASK */}
      <div
        className={clsx(
          stickyCell(),
          groupHover && "outline outline-2 outline-amber outline-offset-[-2px] bg-amber-glow/30"
        )}
        style={{ height: rowH, position: "sticky", left: leftOffsets[4], zIndex: 10 }}
        onClick={onSelect}
        onContextMenu={handleContextMenu}
        onDoubleClick={(e) => {
          e.stopPropagation();
          onSelectTask && onSelectTask();
        }}
        data-testid={`row-${block.id}-task`}
        draggable
        onDragStart={makeRoomMoveDragStart({
          rowKey: block.id,
          selection,
          getSelection: selectionRef ? () => selectionRef.current : undefined,
        })}
        onDragOver={(e) => {
          if (!onGroupWithTask) return;
          if (!Array.from(e.dataTransfer.types).includes(TASK_GROUP_MIME)) return;
          // Don't highlight self as a target.
          const raw = e.dataTransfer.getData(TASK_GROUP_MIME);
          // dataTransfer.getData returns "" during dragover in most browsers
          // (only `types` is reliable until drop). Use a heuristic: ignore if
          // groupHover would apply to this row's own block_id by comparing
          // selection's first key after drop. For now just always accept the
          // visual hover — the drop handler is the final authority.
          void raw;
          e.preventDefault();
          e.dataTransfer.dropEffect = "move";
          setGroupHover(true);
        }}
        onDragLeave={(e) => {
          if (e.currentTarget === e.target) setGroupHover(false);
        }}
        onDrop={(e) => {
          setGroupHover(false);
          const raw = e.dataTransfer.getData(TASK_GROUP_MIME);
          if (!raw || !onGroupWithTask) return;
          e.preventDefault();
          e.stopPropagation();
          try {
            const payload = JSON.parse(raw);
            if (payload.kind === "task-group" && task?.id) {
              onGroupWithTask({ payload, targetTaskId: task.id });
            }
          } catch (err) { console.warn(err); }
        }}
      >
        <div className="flex items-center gap-2 w-full min-w-0">
          {(subs?.length || 0) > 0 ? (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onToggleExpand && onToggleExpand();
              }}
              className="flex items-center gap-0.5 text-ink-mute hover:text-amber shrink-0"
              title={`${subs.length} subtask${subs.length === 1 ? "" : "s"}`}
              data-testid={`expand-${block.id}`}
            >
              <ChevronRight className={clsx("w-3 h-3 transition-transform", isExpanded && "rotate-90")} />
              <span className="mono text-2xs uppercase tracking-widest">{subs.length}</span>
            </button>
          ) : (
            <span className="w-4 shrink-0" />
          )}
          {isFloating && (
            <span className="text-violet mono text-2xs uppercase tracking-widest border border-violet/40 px-1">
              FLT
            </span>
          )}
          {isDeadline && (
            <span className="text-red mono text-2xs uppercase tracking-widest border border-red/40 px-1">
              DDL
            </span>
          )}
          {(isRecurringMaster || isRecurringChild) && (
            <span
              className={clsx(
                "mono text-2xs uppercase tracking-widest flex items-center gap-0.5 px-1 border",
                isRecurringMaster ? "text-cyan border-cyan/40" : "text-cyan/60 border-cyan/20"
              )}
              title={isRecurringMaster ? "Recurring master" : "Recurring instance"}
              data-testid={`recurring-${block.id}`}
            >
              <Repeat className="w-2.5 h-2.5" />
              {isRecurringMaster ? "↻" : "·"}
            </span>
          )}
          {task?.priority === "critical" && (
            <span className="text-red mono text-2xs uppercase tracking-widest">‼</span>
          )}
          {task?.priority === "high" && (
            <span className="text-amber mono text-2xs uppercase tracking-widest">!</span>
          )}
          <InlineText
            value={task?.title || ""}
            disabled={isLocked}
            onCommit={onTitleCommit}
            dataTestid={`title-${block.id}`}
          />
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onSelectTask && onSelectTask();
            }}
            className="ml-1 text-ink-mute hover:text-amber shrink-0 opacity-60 hover:opacity-100 transition-opacity"
            title="Open task detail"
            data-testid={`open-detail-${block.id}`}
          >
            <Pencil className="w-2.5 h-2.5" />
          </button>
          <span className="mono text-2xs text-ink-mute ml-auto whitespace-nowrap" title="Duration">
            {fmtDuration(dur)}
          </span>
        </div>
      </div>

      {/* STATUS */}
      <div
        className={stickyCell()}
        style={{ height: rowH, position: "sticky", left: leftOffsets[5], zIndex: 10 }}
        onClick={onSelect}
        onContextMenu={handleContextMenu}
        data-testid={`row-${block.id}-status`}
      >
        <StatusPill
          status={taskStatus}
          onChange={(next) => onPatchTask(task.id, { status: next })}
          dataTestid={`status-pill-${block.id}`}
        />
      </div>

      {/* NOTES / BLOCKERS */}
      <div
        className={stickyCell()}
        style={{ height: rowH, position: "sticky", left: leftOffsets[6], zIndex: 10 }}
        onContextMenu={handleContextMenu}
        data-testid={`row-${block.id}-notes`}
      >
        <div className="flex items-center gap-2 w-full min-w-0">
          {bb.length > 0 && (
            <span
              className={clsx(
                "flex items-center gap-1 mono text-2xs px-1 border",
                bb.some((b) => b.severity === "critical")
                  ? "text-red border-red bg-red-glow"
                  : "text-amber border-amber bg-amber-glow"
              )}
              title={bb.map((b) => `${b.severity}: ${b.reason}`).join("\n")}
            >
              <Flag className="w-3 h-3" />
              {bb.length}
            </span>
          )}
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onEditNotes && onEditNotes();
            }}
            className={clsx(
              "flex items-center gap-1 min-w-0 flex-1 text-left text-2xs hover:text-amber transition-colors",
              task?.notes ? "text-ink-dim" : "text-ink-mute mono uppercase tracking-wider"
            )}
            title={task?.notes ? `Edit note: ${task.notes}` : "Add note"}
            data-testid={`notes-edit-${block.id}`}
          >
            <Pencil className="w-2.5 h-2.5 shrink-0" />
            {task?.notes ? <span className="truncate">{task.notes}</span> : <span>+ note</span>}
          </button>
        </div>
        {!isLocked && (
          <div className="resize-handle" onMouseDown={onResize} title="Drag to resize duration">
            <div className="absolute inset-x-0 bottom-0 h-px bg-line group-hover:bg-amber" />
          </div>
        )}
      </div>

      {/* CREW CELLS */}
      {crew.map((c) => {
        const a = ba.find((aa) => aa.crew_member_id === c.id);
        const { prev, next } = crewTimelines
          ? crewContextFor(crewTimelines.get(c.id), blockStartMs, blockEndMs)
          : { prev: null, next: null };
        return (
          <CrewCell
            key={c.id}
            crewMember={c}
            block={block}
            blockStartMs={blockStartMs}
            blockEndMs={blockEndMs}
            startDisplay={startDisplay}
            assignment={a}
            prev={prev}
            next={next}
            room={room}
            roomById={roomById}
            cellBase={cellBase}
            stateStyle={stateStyle}
            rowH={rowH}
            onSelect={onSelect}
            onContextMenu={handleContextMenu}
            onAssignCrew={onAssignCrew}
            onUnassignCrew={onUnassignCrew}
            doubleBookKeys={doubleBookKeys}
            isLocked={isLocked}
            crewRoles={crewRoles}
            onAddCrewRole={onAddCrewRole}
          />
        );
      })}
      <ContextMenu
        open={!!ctxMenu}
        x={ctxMenu?.x || 0}
        y={ctxMenu?.y || 0}
        onClose={() => setCtxMenu(null)}
        items={ctxItems}
      />
    </>
  );
}

// Compact local-time formatter for the room-view date column: "Wed 20 May".
// Time is already shown in the START column so we keep this column to the
// date only — gives producers the day context they need without duplication.
function fmtDateTimeCompact(iso) {
  try {
    const d = new Date(iso);
    const dow = ["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"][d.getDay()];
    const mon = ["JAN", "FEB", "MAR", "APR", "MAY", "JUN", "JUL", "AUG", "SEP", "OCT", "NOV", "DEC"][d.getMonth()];
    return `${dow} ${d.getDate()} ${mon}`;
  } catch {
    return "—";
  }
}
